import { HttpEvent, HttpHandler, HttpHeaders, HttpInterceptor, HttpRequest } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { CookieService } from "ngx-cookie";
import { Observable } from "rxjs";
import { environment } from "../../../environments/environment";
import { Router } from "@angular/router";
import { LocalStorageHandlerService } from "@ehrSharedResources/services/local-storage-handler/local-storage-handler.service";
import { EERROR_BEHAVIORS, EhrBaseError } from "@ehrCore/errors/ehr.error";

@Injectable({
  providedIn: "root"
})
export class InterceptorService implements HttpInterceptor {
  constructor(
    private cookieService: CookieService,
    private router: Router,
    private v2LocalStorageHandler: LocalStorageHandlerService
  ) { }


  private _generateHeaders() {
    const csrftoken = this.cookieService.get(environment.CSRF_COOKIE_NAME);
    return new HttpHeaders({
      "Content-Type": "application/json",
      // "Access-Control-Allow-Credentials": "true",
      Accept: "application/json",
      responseType: "arraybuffer",
      "X-CSRFToken": csrftoken
    });
  }

  private _matchesEndpointPattern(req: HttpRequest<any>, endpointUrlList: any[]): boolean {
    if (endpointUrlList.length === 0) {
      return false;
    }
    return endpointUrlList.some(pattern => {
      if (pattern instanceof RegExp) {
        const normalizedUrl = req?.url.startsWith("/") ? req.url.slice(1) : req.url;
        return pattern.test(normalizedUrl);
      }
      return req?.url.includes(pattern);
    });
  }

  private _extractClientAndProjectIds(url: URL): { clientId: string, projectIds: string[] } {
    const athenaValues = JSON.parse(localStorage.getItem("athenaValues")) || {};

    if (!Object.keys(athenaValues).length) {
      const v2Values = this.v2LocalStorageHandler.getUserDetails();
      return {
        clientId: v2Values.clientId.toString(),
        projectIds: v2Values.projectId ? [v2Values.projectId.toString()] : []
      };
    }

    const clientId = url.searchParams.get("clientId") || athenaValues.client_id.toString();
    const projectIds = url.searchParams.getAll("projectId")?.length ?
      url.searchParams.getAll("projectId") : athenaValues.project_id.toString();
    return { clientId, projectIds };
  }

  private _extractUrlWithParams() {
    const currentUrl = this.router.url;
    const fullUrl = new URL(currentUrl, window.location.origin);
    return fullUrl;
  }

  private _appendIdstoHeaders(headers: HttpHeaders, clientId: string, projectIds: string[]) {
    if (clientId && (projectIds && projectIds?.length)) {
      const newHeaders = headers
        .append("X-CLIENT-ID", clientId)
        .append("X-PROJECT-ID", typeof projectIds === "string" ? projectIds : projectIds.join(","));

      return newHeaders;
    }
    return headers;
  }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const csrftoken = this.cookieService.get(environment.CSRF_COOKIE_NAME);
    const accessToken = sessionStorage.getItem("access_token");

    const endpointUrlList = [
      "cdi-reviewer/report/productivity",
      "cdi-reviewer/report/users",
      "cdi-reviewer/report/chart",
      /codes\/icd10\/\d+\/hcc/,
      "athena-members",
      "v2/projects",
      "v2/query-template",
      "v2/charts",
      "v2/queries"
    ];

    if (csrftoken !== null && csrftoken !== undefined && accessToken !== null && accessToken !== undefined) {
      let headers = new HttpHeaders({
        "Content-Type": "application/json",
        // "Access-Control-Allow-Credentials": "true",
        Accept: "application/json",
        responseType: "arraybuffer",
        Authorization: "Bearer " + sessionStorage.getItem("access_token"),
        "X-CSRFToken": csrftoken
      });

      if (endpointUrlList.some(pattern => {
        if (pattern instanceof RegExp) {
          const normalizedUrl = req?.url.startsWith("/") ? req.url.slice(1) : req.url;
          return pattern.test(normalizedUrl);
        }
        return req?.url.includes(pattern);
      })) {
        const currentUrl = this.router.url;
        const fullUrl = new URL(currentUrl, window.location.origin);

        const athenaValues = JSON.parse(localStorage.getItem("athenaValues")) || {};
        const hashedID = sessionStorage.getItem("hashClientID");
        const roleID = sessionStorage.getItem("roleId") || null;

        const clientId = hashedID || athenaValues?.hashed_client_id?.toString() || fullUrl?.searchParams?.get("clientId");
        const projectIds = fullUrl?.searchParams?.getAll("projectId")?.length ? fullUrl?.searchParams?.getAll("projectId") :
          athenaValues?.project_id?.toString();

        if (clientId && (projectIds && projectIds?.length)) {
          headers = headers
            .append("X-CLIENT-ID", clientId)
            .append("X-PROJECT-ID", typeof projectIds === "string" ? projectIds : projectIds.join(","));
        } else if (clientId){
          headers = headers.append("X-CLIENT-ID", clientId);
        }

        const needsRoleHeader = ["v2/charts", "query-template/filter"].some(path => req.url.includes(path));

        if (needsRoleHeader && roleID) {
          headers = headers.append("X-ROLE-ID", roleID);
        }
      }

      const cloneReq = req.clone({ headers, withCredentials: true });
      return next.handle(cloneReq);
    }
    else if (csrftoken !== null && csrftoken !== undefined) {
      let headers = this._generateHeaders();

      if (this._matchesEndpointPattern(req, endpointUrlList)) {
        const urlWithParams = this._extractUrlWithParams();
        const { clientId, projectIds } = this._extractClientAndProjectIds(urlWithParams);
        if (!clientId || projectIds.length === 0) {
          throw new EhrBaseError("Client ID or Project IDs are missing in the request.", EERROR_BEHAVIORS.SHOW_ERROR_PAGE, "Missing IDs", "error");
        }
        headers = this._appendIdstoHeaders(headers, clientId, projectIds);
      }
      const cloneReq = req.clone({ headers, withCredentials: true });
      return next.handle(cloneReq);
    }
    else {
      const headers = new HttpHeaders({
        "Content-Type": "application/json",
        // "Access-Control-Allow-Credentials": "true",
        Accept: "application/json",
        responseType: "arraybuffer",
        // Authorization: "Basic " + btoa("varun:POaccount@9114"),
      });

      const cloneReq = req.clone({ headers, withCredentials: true });
      return next.handle(cloneReq);
    }
  }
}
